import { getSessionToken } from '../api'
import type { SubtitleTrack } from '../protocol'

interface SubEvent {
  start: number
  end: number
  text: string
}

interface SubtitleData {
  format: 'ass' | 'vtt'
  header?: string
  events: SubEvent[]
  complete: boolean
  version: number
}

const POLL_MS = 4000

type JassubInstance = {
  ready: Promise<void>
  timeOffset: number
  renderer: { setTrack(content: string): unknown }
  resize(forceRepaint?: boolean): Promise<void>
  destroy(): Promise<void>
}

/**
 * SubtitleRenderer shows one subtitle track over the video. Embedded tracks
 * grow while the video streams, so events are polled until complete.
 */
export class SubtitleRenderer {
  private track: SubtitleTrack | null = null
  private data: SubtitleData | null = null
  private pollTimer = 0
  private frame = 0
  private jassub: JassubInstance | null = null
  private jassubLoading: Promise<JassubInstance> | null = null
  private generation = 0
  private lastHtml = ''
  private delay = 0
  private plainAss = false
  // Text events of an ASS track shown as plain text, parsed once per data version.
  private plain: { version: number; events: SubEvent[] } | null = null

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly overlay: HTMLElement,
    private readonly mediaId: string,
    private readonly fontCount: number,
  ) {}

  select(track: SubtitleTrack | null) {
    if (track?.id === this.track?.id) return
    this.reset()
    this.track = track
    if (!track || (track.format !== 'ass' && track.format !== 'vtt')) return
    void this.poll(++this.generation)
  }

  destroy() {
    this.reset()
  }

  /**
   * Applies viewer options: delay in seconds (positive shows subtitles
   * later) and whether ASS tracks render as plain text.
   */
  setOptions(delay: number, plainAss: boolean) {
    this.delay = delay
    if (this.jassub) {
      this.jassub.timeOffset = -delay
      void this.jassub.resize(true)
    }
    if (plainAss !== this.plainAss) {
      this.plainAss = plainAss
      if (this.data?.format === 'ass') {
        this.dropRenderers()
        void this.render()
      }
    }
  }

  private reset() {
    this.generation++
    window.clearTimeout(this.pollTimer)
    cancelAnimationFrame(this.frame)
    this.data = null
    this.track = null
    this.dropRenderers()
  }

  private dropRenderers() {
    cancelAnimationFrame(this.frame)
    this.frame = 0
    this.overlay.replaceChildren()
    this.lastHtml = ''
    this.plain = null
    const j = this.jassub
    this.jassub = null
    this.jassubLoading = null
    void j?.destroy()
  }

  private async poll(gen: number) {
    const track = this.track
    if (!track) return
    try {
      const v = this.data?.version ?? 0
      const session = getSessionToken()
      const res = await fetch(`/api/media/${this.mediaId}/subs/${track.id}?v=${v}`, {
        headers: session ? { Authorization: `Session ${session}` } : undefined,
      })
      if (gen !== this.generation) return
      if (res.status === 200) {
        this.data = (await res.json()) as SubtitleData
        if (gen !== this.generation) return
        await this.render()
      }
    } catch {
      // retry on the next poll
    }
    if (gen === this.generation && !this.data?.complete) {
      this.pollTimer = window.setTimeout(() => void this.poll(gen), POLL_MS)
    }
  }

  private async render() {
    const d = this.data
    if (!d) return
    if (d.format === 'ass' && !this.plainAss) {
      const content = `${d.header ?? ''}${d.events.map((e) => e.text).join('\n')}\n`
      const gen = this.generation
      const j = await this.ensureJassub(content)
      if (gen === this.generation) await j.renderer.setTrack(content)
    } else if (!this.frame) {
      this.drawCues()
    }
  }

  private ensureJassub(content: string): Promise<JassubInstance> {
    if (!this.jassubLoading) {
      const gen = this.generation
      const fonts = Array.from({ length: this.fontCount }, (_, i) => `/api/media/${this.mediaId}/fonts/${i}`)
      this.jassubLoading = import('jassub').then(async ({ default: JASSUB }) => {
        const j = new JASSUB({ video: this.video, subContent: content, fonts, timeOffset: -this.delay }) as unknown as JassubInstance
        await j.ready
        if (gen !== this.generation) {
          // The track changed while loading; do not leave a second renderer on the video.
          await j.destroy()
          throw new Error('subtitle track changed')
        }
        this.jassub = j
        return j
      })
    }
    return this.jassubLoading
  }

  /** Events to draw as text: SRT/WebVTT as is, ASS reduced to its text. */
  private textEvents(): SubEvent[] {
    const d = this.data
    if (!d) return []
    if (d.format !== 'ass') return d.events
    if (this.plain?.version !== d.version) {
      const events: SubEvent[] = []
      for (const e of d.events) {
        const text = assText(e.text)
        if (text) events.push({ start: e.start, end: e.end, text })
      }
      events.sort((a, b) => a.start - b.start)
      this.plain = { version: d.version, events }
    }
    return this.plain.events
  }

  private drawCues = () => {
    const events = this.textEvents()
    const t = this.video.currentTime - this.delay
    // Events are sorted by start; scan the ones that started already.
    let hi = events.length
    let lo = 0
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (events[mid].start <= t) lo = mid + 1
      else hi = mid
    }
    const active: string[] = []
    for (let i = lo - 1; i >= 0 && i >= lo - 50; i--) {
      if (events[i].end > t) active.unshift(cueHtml(events[i].text))
    }
    const html = active.map((a) => `<span class="cue">${a}</span>`).join('')
    if (html !== this.lastHtml) {
      this.overlay.innerHTML = html
      this.lastHtml = html
    }
    this.frame = requestAnimationFrame(this.drawCues)
  }
}

function escapeHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** Renders SRT/WebVTT cue text, keeping only simple formatting tags. */
function cueHtml(text: string) {
  const cleaned = text.replace(/\{\\[^}]*\}/g, '') // ASS overrides in SRT files
  return escapeHtml(cleaned)
    .replace(/&lt;(\/?)(i|b|u)&gt;/gi, '<$1$2>')
    .replace(/&lt;[^&]*?&gt;/g, '')
    .replace(/\n/g, '<br>')
}

/**
 * The text of an ASS "Dialogue:" line without styling: override blocks are
 * dropped, \N becomes a line break. Drawings (\p1) have no text at all.
 */
export function assText(line: string): string {
  const body = line.replace(/^Dialogue:\s*/i, '')
  // Nine fields precede the text, which may itself contain commas.
  let i = 0
  for (let n = 0; n < 9 && i >= 0; n++) i = body.indexOf(',', i) + 1 || -1
  if (i < 0) return ''
  const text = body.slice(i)
  if (/\{[^}]*\\p[1-9]/.test(text)) return ''
  return text
    .replace(/\{[^}]*\}/g, '')
    .replace(/\\[Nn]/g, '\n')
    .replace(/\\h/g, ' ')
    .trim()
}
